import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useContactClaimCustomer } from "@/admin/hooks/use-claims";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Textarea } from "@/shared/components/ui/textarea";
import { toastApiError } from "@/shared/lib/api-error";

export function ContactCustomerDialog({
  open,
  onOpenChange,
  customerName,
  claimUuid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  claimUuid: string;
}) {
  const [message, setMessage] = useState("");
  const contactCustomer = useContactClaimCustomer();

  const handleSend = async () => {
    if (message.trim().length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }

    try {
      await contactCustomer.mutateAsync({
        uuid: claimUuid,
        message: message.trim(),
      });
      toast.success("Message sent successfully");
      setMessage("");
      onOpenChange(false);
    } catch (err: unknown) {
      toastApiError(err, "Failed to send message");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Contact Customer
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Send a customer-visible message to {customerName}. It will appear in
            the claim history and notes, and we&apos;ll email the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe the update or ask the customer for more information..."
            rows={5}
            className="w-full resize-y rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3 text-[13px] leading-[1.6] text-[var(--ink)] outline-none transition-colors focus:border-[var(--green)]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={contactCustomer.isPending || message.trim().length < 10}
            className="cursor-pointer"
          >
            {contactCustomer.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            Send Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
